/* eslint-disable no-nested-ternary */
import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Box,
  BoxProps,
  Flex,
  Grid,
  GridItem,
  Hide,
  Text,
} from '@chakra-ui/react';
import { isAfter, sub } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { ReactElement, useState } from 'react';
import { useCookies } from 'react-cookie';

import PurpleFaBriefcase from '@public/icons/jobs/PurpleFaBriefcase.svg';
import PurpleFaDollarSign from '@public/icons/jobs/PurpleFaDollarSign.svg';
import { Badge } from 'components/atoms/badge';
import { BadgeTech } from 'components/atoms/badgeTech';
import Button from 'components/atoms/button';
import { CountryFlag, FlagEnum } from 'components/atoms/countryFlag';
import Heading from 'components/atoms/heading';
import { SalaryRange, SalarySuffixTypes } from 'components/atoms/salaryRange';
import { SanitizeHTML } from 'components/atoms/sanitize';
import { TextTag } from 'components/atoms/textTag';
import { AUTH_TOKEN } from 'lib/constants/auth';
import { USER_TYPE } from 'lib/constants/cookies';
import { jobExperienceLevelMap } from 'lib/constants/enums/jobExperienceLevel';
import * as paths from 'lib/constants/paths';
import {
  ExperienceLevelEnum,
  JobTypeEnum,
  useGetCurrentCandidateInterestJobsByJobIdsQuery,
} from 'lib/generated/graphql';
import { formatCity } from 'lib/utils/formatCity';
import { getJobCustomTimeDurationString } from 'lib/utils/getJobCustomTimeDuration';
import { formatTechs } from 'lib/utils/jobCard';

export interface ShowcaseCardProps extends BoxProps {
  jobId: string;
  focus?: string;
  acceptRemote: boolean;
  city?: string;
  slug: string;
  techs: Array<{ name: string; urlPath: string }>;
  title: string;
  cltSalaryMin: number;
  cltSalaryMax: number;
  pjSalaryMin: number;
  pjSalaryMax: number;
  usdAnnualSalaryMin: number;
  usdAnnualSalaryMax: number;
  experienceLevel: ExperienceLevelEnum;
  createdAt: string;
  requirements: string;
  description: string;
  hideSalary: boolean;
  isJobInternational?: boolean;
  hybrid: boolean;
  jobType: JobTypeEnum;
  companySlug: string;
}

export const ShowcaseCard = ({
  jobId,
  focus,
  slug,
  createdAt,
  title,
  techs,
  city,
  acceptRemote,
  cltSalaryMin,
  cltSalaryMax,
  pjSalaryMin,
  pjSalaryMax,
  usdAnnualSalaryMin,
  usdAnnualSalaryMax,
  experienceLevel,
  requirements,
  description,
  hideSalary,
  isJobInternational,
  hybrid,
  jobType,
  companySlug,
}: ShowcaseCardProps): ReactElement => {
  const [cookies] = useCookies([AUTH_TOKEN, USER_TYPE]);
  const candidateAuth =
    !!cookies.auth_token &&
    !!cookies.user_type &&
    cookies.user_type === 'candidate';
  const [hasInterest, setHasInterest] = useState(false);

  const { loading } = useGetCurrentCandidateInterestJobsByJobIdsQuery({
    variables: {
      jobs: parseInt(jobId, 10),
    },
    skip: !candidateAuth,
    onCompleted: (data) => {
      setHasInterest(
        data !== undefined &&
          data?.getCurrentCandidateInterestJobsByJobIds?.length > 0
      );
    },
  });

  const isNew = isAfter(new Date(createdAt), sub(new Date(), { days: 5 }));
  const createdSince = getJobCustomTimeDurationString(createdAt, new Date());

  const jobPath =
    jobType === JobTypeEnum.FullSupport
      ? paths.SLUG_JOB(slug)
      : jobType === JobTypeEnum.Ats
      ? paths.ATS_SLUG_JOB(companySlug, slug)
      : paths.V2_SLUG_JOB(companySlug, slug);

  return (
    <Link href={jobPath} passHref>
      <a target='_blank' href={jobPath} rel='noopener noreferrer'>
        <Box
          _hover={{ boxShadow: 'sm' }}
          py={30}
          px={[2, 7]}
          backgroundColor='white.100'
          cursor='pointer'
        >
          <Flex justifyContent='space-between' mb={5}>
            {focus ? (
              <Text
                fontFamily="'Poppins', sans-serif"
                fontWeight={600}
                color='primary.500'
                fontSize='xs'
              >
                {focus}
              </Text>
            ) : (
              <Box />
            )}
            {isNew ? (
              <Text fontWeight={700} color='primary.500' fontSize='xs'>
                NOVA
              </Text>
            ) : (
              <Text fontWeight={400} color='grey.900' fontSize='xs'>
                {createdSince}
              </Text>
            )}
          </Flex>
          <Flex flexDir='row' justify='flex-start' align='flex-start'>
            <Heading
              format='h3'
              fontWeight={400}
              fontSize='2xl'
              lineHeight={9}
              letterSpacing='tight'
              color='black.700'
              mb={22}
            >
              {title}
            </Heading>
          </Flex>
          <Flex color='text.100' align='center' flexWrap='wrap' mb={5}>
            {formatTechs(techs).map((tech) => (
              <Link
                key={tech.name}
                href='/vagas-:tech'
                as={tech.urlPath}
                passHref
              >
                <BadgeTech
                  text={tech.name}
                  borderColor='primary.500'
                  hoverBgColor='primary.500'
                  hoverTextColor='white.100'
                  textColor='primary.500'
                  mr={3.5}
                  mb={2}
                />
              </Link>
            ))}
            {!loading && candidateAuth && hasInterest && (
              <TextTag
                icon={<CheckCircleIcon />}
                text='Já demonstrou interesse'
                textColor='primary.50'
                backgroundColor='green.950'
                mb={2}
              />
            )}
          </Flex>
          <Flex
            flexDirection={['column', 'row']}
            align='center'
            alignItems='flex-start'
            mb={4}
          >
            {(city || acceptRemote) && (
              <Grid mb={[4, 0]} gap={2.5} templateColumns='auto auto auto'>
                <GridItem pt='2px'>
                  <CountryFlag
                    flag={
                      isJobInternational ? FlagEnum.usFlag : FlagEnum.brFlag
                    }
                    height={20}
                  />
                </GridItem>
                {city && (
                  <GridItem>
                    <Text fontSize='sm' color='black.700'>
                      {formatCity(city)}
                    </Text>
                  </GridItem>
                )}
                {!isJobInternational && (acceptRemote || hybrid) && (
                  <Badge
                    variant='remote'
                    text={hybrid ? 'híbrido' : 'remoto'}
                  />
                )}
                {isJobInternational && acceptRemote && (
                  <Badge variant='remote' text={city ? 'híbrido' : 'remoto'} />
                )}
              </Grid>
            )}

            {jobExperienceLevelMap[experienceLevel] && (
              <Flex ml={[0, 12]}>
                <Image
                  src={PurpleFaBriefcase}
                  alt='Briefcase icon'
                  height='20px'
                  width='20px'
                />

                <Text ml={2.5} fontSize='sm' color='black.700'>
                  {jobExperienceLevelMap[experienceLevel]?.seniority ||
                    'Não informada'}
                </Text>
              </Flex>
            )}
          </Flex>

          {!hideSalary && (
            <>
              {(cltSalaryMin > 0 || cltSalaryMax > 0) && (
                <Grid
                  justifyContent='start'
                  mb={1}
                  alignItems='center'
                  templateColumns='repeat(3, auto)'
                  columnGap={2.5}
                >
                  <GridItem pt='6px'>
                    <Image
                      src={PurpleFaDollarSign}
                      alt='Dollar sign icon'
                      height='20px'
                      width='20px'
                    />
                  </GridItem>
                  <GridItem>
                    <SalaryRange
                      minSalary={cltSalaryMin}
                      maxSalary={cltSalaryMax}
                      currency='R$'
                      suffix={SalarySuffixTypes.CLT}
                    />
                  </GridItem>
                  <GridItem>
                    <Badge text='CLT' />
                  </GridItem>
                </Grid>
              )}

              {(pjSalaryMin > 0 || pjSalaryMax > 0) && (
                <Grid
                  justifyContent='start'
                  mb={4}
                  templateColumns='repeat(3, auto)'
                  columnGap={2.5}
                >
                  <GridItem pt='2px'>
                    <Image
                      src={PurpleFaDollarSign}
                      alt='Dollar sign icon'
                      height='20px'
                      width='20px'
                    />
                  </GridItem>
                  <GridItem gridColumnStart={[2, 2]}>
                    <SalaryRange
                      minSalary={pjSalaryMin}
                      maxSalary={pjSalaryMax}
                      currency='R$'
                      suffix={SalarySuffixTypes.PJ}
                    />
                  </GridItem>
                  <GridItem>
                    <Badge variant='rawText' text='PJ ou Cooperativa' />
                  </GridItem>
                </Grid>
              )}

              {(usdAnnualSalaryMin > 0 || usdAnnualSalaryMax > 0) && (
                <Grid
                  justifyContent='start'
                  mb={4}
                  templateColumns='repeat(3, auto)'
                  columnGap={2.5}
                >
                  <GridItem pt='2px'>
                    <Image
                      src={PurpleFaDollarSign}
                      alt='Dollar sign icon'
                      height='20px'
                      width='20px'
                    />
                  </GridItem>
                  <GridItem gridColumnStart={2}>
                    <SalaryRange
                      minSalary={usdAnnualSalaryMin}
                      maxSalary={usdAnnualSalaryMax}
                      currency='US$'
                      suffix={SalarySuffixTypes.PJ_US}
                    />
                  </GridItem>
                  <GridItem>
                    <Badge variant='rawText' text='PJ ou Cooperativa' />
                  </GridItem>
                </Grid>
              )}
            </>
          )}

          {!!requirements && (
            <SanitizeHTML
              mb={4}
              fontSize='sm'
              color='black.700'
              lineHeight={6}
              html={`${requirements.substring(0, 300)} ${
                requirements.length > 300 ? '...' : ''
              }`}
            />
          )}

          {!!description && (
            <SanitizeHTML
              mb={4}
              fontSize='sm'
              color='black.700'
              lineHeight={6}
              html={`${description.substring(0, 300)} ${
                description.length > 300 ? '...' : ''
              }`}
            />
          )}

          <Flex justifyContent='right' alignItems='center'>
            <Hide below='sm'>
              <Link href={jobPath} passHref>
                <Button ml={4} p='16px 30px'>
                  Ver detalhes
                </Button>
              </Link>
            </Hide>
          </Flex>
        </Box>
      </a>
    </Link>
  );
};
